import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Redirect } from 'react-router-dom';
import * as consts from 'config/consts';
import { getIncomeGain, getUserBroker } from 'config/api';
import { useCustomHederSideMenu } from 'hooks/useCustomHederSideMenu';
import { useModal } from 'hooks/useModal';
import { getDividendStorage, setDividendStorage } from 'hooks/useDividend';
import { formatDate, formatNumber } from 'utils/formatter';
import { convertDate } from 'utils/date';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';
import Loading from 'components/commons/loading/Loading';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { DevidendPremium } from 'components/features/dividendPrediction/DividendPremium';
import { DividendTransition } from 'components/features/dividendDeposit/DividendTransition';
import { DividendClassification } from 'components/features/dividendDeposit/DividendClassification';
import {
  DividendError,
  DividendDepositLoading,
} from 'components/features/dividendDeposit/DividendError';
import { DividendStoppingAccountModal } from 'components/features/modal/DividendStoppingAccountModal';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { GettingSbiAccountMessage } from 'components/features/dividendDeposit/GettingSbiAccountMessage';
import { isIosAppNotPremium } from 'components/features/utils/userStatus';
import {
  DividendHeader,
  DisplayType,
  FooterText,
  displayTypes,
} from './DividendPrediction';

export const getSummaryIncomeGainAmount = (
  data: IncomeGainItemProps[]
): number => {
  return data.reduce((acc, current): number => {
    const num = current.amount === null ? 0 : current.amount;
    return acc + num;
  }, 0);
};

// 文字列形式の日付`yyyymm`に対して、`dd`に`01`を付与してDate型に変換する
export const convertyyyymmToDate = (date: string): Date | null => {
  const data = yyyymmddToDate(`${date}01`);
  if (data === null) return data;

  return convertDate(data);
};

const setSessionStorageDepositType = (type: number): void => {
  const dividend = getDividendStorage();
  const newDividend = { ...dividend, depositType: type };
  setDividendStorage(newDividend);
};
const setSessionStorageDepositPeriod = (type: number): void => {
  const dividend = getDividendStorage();
  const newDividend = { ...dividend, depositPeriod: type };
  setDividendStorage(newDividend);
};

const initialStates = [
  {
    dateType: 1,
    text: '6ヶ月',
  },
  {
    dateType: 2,
    text: '1年間',
  },
  {
    dateType: 3,
    text: '2年間',
  },
];

const allStates = {
  dateType: 4,
  text: '全期間',
};

const getDateStates = (incomeGainData: IncomeGainItemProps[]) => {
  const yearList = Array.from(
    new Set(
      incomeGainData.map(data => {
        const haitoDate = yyyymmddToDate(data.date);
        return formatDate(haitoDate, 'yyyy');
      })
    )
  ).sort((a, b) => (Number(a) < Number(b) ? 1 : -1));

  const yearStates = yearList.map(item => {
    return { dateType: Number(item), text: item };
  });

  return initialStates.concat(yearStates.concat(allStates));
};

const DividendDepositInternal = ({
  incomeGainData,
  userBroker,
}: {
  incomeGainData: IncomeGainItemProps[];
  userBroker: userBrokerProps[];
}) => {
  const dividendStorage = getDividendStorage();
  const initialDisplayType =
    dividendStorage === null ||
    !dividendStorage.depositType ||
    dividendStorage.depositType === displayTypes.推移
      ? displayTypes.推移
      : displayTypes.内訳;
  const [displayType, setDisplayType] = useState<DisplayType>(
    initialDisplayType
  );

  const updateDisplayType = (type: DisplayType): void => {
    setDisplayType(type);
    setSessionStorageDepositType(
      type === displayTypes.推移 ? displayTypes.推移 : displayTypes.内訳
    );
  };

  const [targetIncomeGainData, setTargetIncomeGainData] = useState<
    IncomeGainItemProps[] | null
  >(null);
  const [totalDividend, setTotalDividend] = useState<number | null>(null);
  const [targetPeriod, setTargetPeriod] = useState<{
    from: Date;
    to: Date;
  } | null>(null);

  const dateStates = getDateStates(incomeGainData);
  const dateTypeList = dateStates.map(date => date.dateType);

  const depositType =
    dividendStorage === null || dividendStorage.depositPeriod === undefined
      ? 3 // 2年間
      : dividendStorage.depositPeriod;
  const isDepositTypeValid = dateTypeList.includes(depositType);

  const [selectedDateType, setSelectedDateType] = useState<number>(
    isDepositTypeValid ? depositType : 3 // 2年間
  );

  const updateSelectedDateType = (type: number) => {
    setSelectedDateType(type);
    setSessionStorageDepositPeriod(type);
  };

  const [isShowModal, { hideModal, showModal }] = useModal();

  const targetList = userBroker.filter(
    item =>
      (item.brokerID === consts.MONOBrokerIds.楽天証券 ||
        item.brokerID === consts.MONOBrokerIds.SBI証券) &&
      item.aggregateSource === consts.AggregateSourceTypes.MONO
  );
  const brokerList = Object.values(
    targetList.reduce(
      (acc, current) => {
        const brokerID = current.brokerID;
        if (!acc[brokerID]) {
          acc[brokerID] = current;
          return acc;
        }
        acc[brokerID] =
          new Date(acc[brokerID].updTime).getTime() <
          new Date(current.updTime).getTime()
            ? current
            : acc[brokerID];
        return acc;
      },
      {} as {
        [key: string]: userBrokerProps;
      }
    )
  ).sort((first, _) => {
    if (first.brokerID === consts.MONOBrokerIds.楽天証券) return -1;
    return 1;
  });
  const isAccountError = brokerList.some(
    item => !['000', '800', null].includes(item.status)
  );

  const latestUpdtimeItem = targetList.reduce((prev, current) => {
    if (!prev[0]) return [current];
    const firstUpdTime = new Date(prev[0].updTime).getTime();
    const secondUpdTime = new Date(current.updTime).getTime();

    return firstUpdTime < secondUpdTime ? [current] : prev;
  }, [] as userBrokerProps[]);

  const toDay = convertDate(new Date());

  useEffect(() => {
    const getTargetData = (targetDay: number) => {
      return incomeGainData.filter(data => {
        const date = convertDate(yyyymmddToDate(data.date) || toDay);
        return targetDay <= date.getTime();
      });
    };

    // 6ヶ月
    if (selectedDateType === 1) {
      const day = convertDate(new Date());
      const targetDay = day.setDate(day.getDate() - 180);
      const targetData = getTargetData(targetDay);
      setTargetIncomeGainData(targetData);

      const totalDividend = getSummaryIncomeGainAmount(targetData);
      setTotalDividend(totalDividend);

      setTargetPeriod({ from: new Date(targetDay), to: toDay });

      // 1年間
    } else if (selectedDateType === 2) {
      const day = convertDate(new Date());
      const targetDay = day.setFullYear(day.getFullYear() - 1);
      const targetData = getTargetData(targetDay);
      setTargetIncomeGainData(targetData);

      const totalDividend = getSummaryIncomeGainAmount(targetData);
      setTotalDividend(totalDividend);

      setTargetPeriod({ from: new Date(targetDay), to: toDay });

      // 2年間
    } else if (selectedDateType === 3) {
      const day = convertDate(new Date());
      const targetDay = day.setFullYear(day.getFullYear() - 2);
      const targetData = getTargetData(targetDay);
      setTargetIncomeGainData(targetData);

      const totalDividend = getSummaryIncomeGainAmount(targetData);
      setTotalDividend(totalDividend);

      setTargetPeriod({ from: new Date(targetDay), to: toDay });

      // 全期間
    } else if (selectedDateType === 4) {
      setTargetIncomeGainData(incomeGainData);

      const totalDividend = getSummaryIncomeGainAmount(incomeGainData);
      setTotalDividend(totalDividend);

      const min = incomeGainData.reduce((arr, current) => {
        const arrDate = convertDate(yyyymmddToDate(arr.date) || toDay);
        const currentDate = convertDate(yyyymmddToDate(current.date) || toDay);

        return arrDate.getTime() < currentDate.getTime() ? arr : current;
      }, incomeGainData[0]);

      setTargetPeriod({
        from: convertDate(yyyymmddToDate(min.date) || toDay),
        to: toDay,
      });

      // APIのデータから計算した年を選択
    } else {
      const targetState = dateStates.find(
        data => data.dateType === selectedDateType
      );
      if (targetState === undefined) return;

      const from = convertDate(
        yyyymmddToDate(`${targetState.text}0101`) || toDay
      );
      const to = convertDate(
        yyyymmddToDate(`${targetState.text}1231`) || toDay
      );
      const targetData = incomeGainData.filter(data => {
        const date = convertDate(yyyymmddToDate(data.date) || toDay);
        return (
          from.getTime() <= date.getTime() && date.getTime() <= to.getTime()
        );
      });
      setTargetIncomeGainData(targetData);

      const totalDividend = getSummaryIncomeGainAmount(targetData);
      setTotalDividend(totalDividend);

      setTargetPeriod({ from, to });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateType]);

  if (
    totalDividend === null ||
    targetIncomeGainData === null ||
    targetPeriod === null
  )
    return (
      <DividendDepositLoading
        str={displayType === displayTypes.推移 ? '日付' : '銘柄'}
      />
    );

  return (
    <>
      <DividendHeader {...{ type: 'deposit' }} />
      <div className="bg-white">
        <GettingSbiAccountMessage
          {...{
            userBroker,
            incomeGainData,
          }}
        />
        <div
          className="relative w-full flex flex-row justify-center items-center pt-5 mx-auto text-bluegray-700"
          style={{ width: '90px' }}>
          <select
            className="h-full w-full pl-2 appearance-none bg-transparent text-base font-bold cursor-pointer"
            value={selectedDateType}
            onChange={(e): void => {
              updateSelectedDateType(Number(e.currentTarget.value));
            }}>
            {dateStates.map((s, i) => {
              return (
                <option key={i} value={s.dateType.toString()}>
                  {s.text}
                </option>
              );
            })}
          </select>
          <div
            className="absolute icon-arrow_drop_down pointer-events-none p-0.5 text-2xl"
            style={{ right: '6%' }}></div>
        </div>
        <div className="text-center text-sm text-bluegray-700">
          {formatDate(targetPeriod.from, 'yyyy/M')}~
          {formatDate(targetPeriod.to, 'yyyy/M')}
        </div>
        <div className="flex flex-row items-baseline justify-center">
          {isAccountError ? (
            <div
              className="icon-alert text-red-500 cursor-pointer"
              onClick={showModal}
            />
          ) : (
            <></>
          )}
          <div className="px-4 mt-2 text-center text-xs text-bluegray-700">
            <span className="mr-1 text-2xl font-bold">
              {formatNumber(totalDividend, {})}
            </span>
            円
          </div>
        </div>

        <div className="flex flex-row justify-center items-center pt-2 text-sm text-gray-500">
          <div
            className={`px-3 ${
              displayType === displayTypes.推移
                ? 'text-white bg-blue-700 rounded'
                : 'cursor-pointer'
            } cursor-pointer`}
            onClick={(): void => updateDisplayType(displayTypes.推移)}>
            推移
          </div>
          <div className="text-base">｜</div>
          <div
            className={`px-3 ${
              displayType === displayTypes.内訳
                ? 'text-white bg-blue-700 rounded'
                : 'cursor-pointer'
            } cursor-pointer`}
            onClick={(): void => updateDisplayType(displayTypes.内訳)}>
            内訳
          </div>
        </div>

        {displayType === displayTypes.推移 ? (
          <DividendTransition
            {...{
              incomeGainData: targetIncomeGainData,
              targetPeriod,
              updTime: latestUpdtimeItem[0].updTime,
            }}
          />
        ) : (
          <DividendClassification
            {...{
              incomeGainData: targetIncomeGainData,
              totalDividend,
              updTime: latestUpdtimeItem[0].updTime,
            }}
          />
        )}
      </div>
      <FooterText />
      <DividendStoppingAccountModal
        {...{ isShow: isShowModal, hide: hideModal, brokerList }}
      />
    </>
  );
};

const GetDividendData = () => {
  const { data: incomeGain, isFetching: isFetchingIncomeGain } = useQuery(
    'getIncomeGainDividendDeposit',
    () => getIncomeGainAPI(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: userBroker_, isFetching: isFetchingUserBroker } = useQuery(
    'getUserBrokerDividendDeposit',
    () => getUserBrokerAPI(),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (isFetchingIncomeGain || isFetchingUserBroker)
    return (
      <Loading
        style={{ marginTop: '100px', marginBottom: '100px' }}
        loading={true}
      />
    );

  if (
    incomeGain === null ||
    incomeGain === undefined ||
    userBroker_ === null ||
    userBroker_ === undefined
  )
    return (
      <Loading
        style={{ marginTop: '100px', marginBottom: '100px' }}
        loading={true}
      />
    );

  const userBroker = userBroker_ as userBrokerProps[];
  const isIncludTargetAccount = userBroker.some(
    data =>
      data.brokerID === consts.MONOBrokerIds.SBI証券 ||
      data.brokerID === consts.MONOBrokerIds.楽天証券
  );
  if (!isIncludTargetAccount) return <DividendError type={'broker'} />;

  if (incomeGain.items.length === 0) return <DividendError type={'deposit'} />;

  const incomeGainData = incomeGain.items as IncomeGainItemProps[];

  return <DividendDepositInternal {...{ incomeGainData, userBroker }} />;
};

const DividendDeposit = (): React.ReactElement => {
  useCustomHederSideMenu();
  const { setIsShowMenu } = useGlobalMenuContext();

  useEffect(() => {
    return setIsShowMenu(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isIosAppNotPremium()) return <Redirect to="/home" />;

  return (
    <PremiumCheck content={<DevidendPremium />}>
      <GetDividendData />
    </PremiumCheck>
  );
};

export type IncomeGainItemProps = {
  account: string;
  brokerID: string;
  brokerName: string;
  symbol: string;
  symbolName: string;
  productType: number;
  amount: number | null;
  date: string; // yyyymmdd
  isin: string;
  hasNew: boolean;
};

export type IncomeGainProps = {
  from: string; // yyyyMM
  to: string; // yyyyMM
  items: IncomeGainItemProps[];
};

const getIncomeGainAPI = async () => {
  const ret_ = await getIncomeGain();

  const ret = {
    from: ret_.From,
    to: ret_.To,
    items: ret_.Items.map(item => {
      return {
        account: item.Account,
        brokerID: item.BrokerID,
        brokerName: item.BrokerName,
        symbol: item.Symbol,
        symbolName: item.SymbolName,
        productType: item.ProductType,
        amount: item.Amount,
        date: item.Date,
        isin: item.ISIN,
        hasNew: item.HasNew,
      };
    }) as IncomeGainItemProps[],
  } as IncomeGainProps;

  return ret;
};

export type userBrokerProps = {
  brokerID: string;
  brokerName: string;
  brokerType: number;
  aggregateSource: number;
  account: string;
  status: string | null;
  updTime: string;
};

const getUserBrokerAPI = async (): Promise<userBrokerProps[]> => {
  const ret_ = await getUserBroker();
  // memo: 不要な項目は省略して渡す
  const ret = ret_.Items.map(item => {
    return {
      brokerID: item.BrokerID,
      brokerName: item.BrokerName,
      brokerType: item.BrokerType,
      aggregateSource: item.AggregateSource,
      account: item.Account,
      status: item.Status,
      updTime: item.UpdTime,
    } as userBrokerProps;
  });

  return ret;
};

export default DividendDeposit;
